/* eslint-disable react/jsx-filename-extension */
import React, {
  useState, useRef, useEffect, useMemo,
} from 'react';
import { Row } from 'antd';
import { DateTime } from 'luxon';
import PropTypes from 'prop-types';

import MonthDay from './MonthDay';

import { getDayKey, splitMultiDayShifts } from '../scheduleHelpers';
import { positionWeekShifts } from './monthSchedule.helpers';

const days = new Array(7).fill(1);
export default function MonthWeek({
  showBorder,
  weekStart,
  monthStart,
  monthEnd,
  onDaySelect,
  onShiftSelect,
  shifts = {},
}) {
  const containerRef = useRef(null);
  const [height, setHeight] = useState();
  const className = (
    showBorder
      ? 'schedule-month-week-border-bottom'
      : 'schedule-month-week'
  );
  useEffect(() => {
    const updateSize = () => {
      if (containerRef && containerRef.current) {
        const { clientHeight } = containerRef.current;
        setHeight(clientHeight);
      }
    };
    updateSize();
    window.addEventListener('resize', updateSize);
    return () => window.removeEventListener('resize', updateSize);
  }, []);

  const {
    multiDayPerDay,
    interDayPerDay,
  } = useMemo(() => {
    const mPerDay = {};
    const iPerDay = {};
    days.forEach((_, idx) => {
      const day = weekStart.plus({ days: idx });
      const dayKey = getDayKey(day);
      const dayShifts = shifts[dayKey] ?? [];
      const {
        multiDay,
        interDay,
      } = splitMultiDayShifts(dayShifts, day);
      mPerDay[dayKey] = multiDay;
      iPerDay[dayKey] = interDay;
    });
    return {
      multiDayPerDay: mPerDay,
      interDayPerDay: iPerDay,
    };
  }, [shifts, weekStart]);

  const { laneByShiftId } = useMemo(() => {
    const weekDays = days.map((_, idx) => weekStart.plus({ days: idx }));
    return positionWeekShifts({
      multiDayPerDay,
      weekDays,
      height,
    });
  }, [multiDayPerDay, weekStart, height]);

  return (
    <Row
      ref={containerRef}
      className={className}
    >
      {days.map((_, idx) => {
        const day = weekStart.plus({ days: idx });
        const dayKey = getDayKey(day);
        return (
          <MonthDay
            key={day.toLocaleString()}
            day={day}
            monthStart={monthStart}
            monthEnd={monthEnd}
            onDaySelect={onDaySelect}
            multiDay={multiDayPerDay[dayKey]}
            interDay={interDayPerDay[dayKey]}
            onShiftSelect={onShiftSelect}
            weekStart={weekStart}
            height={height}
            laneByShiftId={laneByShiftId}
          />
        );
      })}
    </Row>
  );
}

MonthWeek.propTypes = {
  showBorder: PropTypes.bool,
  weekStart: PropTypes.instanceOf(DateTime).isRequired,
  monthStart: PropTypes.instanceOf(DateTime).isRequired,
  monthEnd: PropTypes.instanceOf(DateTime).isRequired,
  onDaySelect: PropTypes.func.isRequired,
  onShiftSelect: PropTypes.func.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  shifts: PropTypes.object,
};

MonthWeek.defaultProps = {
  showBorder: false,
  shifts: {},
};
